import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Filter,
  RefreshCw,
  Eye,
  Play,
  Clock,
  AlertTriangle,
  User,
  Building,
  CreditCard,
  Calendar,
  Phone,
  MapPin,
  Timer,
  DollarSign,
  FileText,
  ChevronLeft,
  ChevronRight,
  X,
  Download,
  SortAsc,
  SortDesc,
  Pause,
  Activity,
} from "lucide-react";
import {
  getDoctorApplications,
  startApplicationReview,
  getHospitalsForFilter,
  getInsuranceCompaniesForFilter,
  getAllApplicationsAdmin,
} from "../../services/doctorService";
import { useAuth } from "../../context/AuthContext";
import { exportPatientApplications } from "../../services/exportService";
import { SignOutIconButton } from "../../component/common/SignOutButton";

const AdminApplicationsList = () => {
  const navigate = useNavigate();
  const { userData: user } = useAuth();

  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const [hospitals, setHospitals] = useState([]);
  const [insuranceCompanies, setInsuranceCompanies] = useState([]);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Sorting state
  const [sortField, setSortField] = useState("submittedAt");
  const [sortDirection, setSortDirection] = useState("desc");

  // Filter state
  const [filters, setFilters] = useState({
    searchTerm: "",
    status: "",
    priority: "",
    emergency: "",
    treatmentType: "",
    insuranceCompany: "",
    hospitalName: "",
    dateFrom: "",
    dateTo: "",
    minAmount: "",
    maxAmount: "",
    doctorName: "",
  });

  // Filter options
  const statusOptions = [
    { value: "", label: "All Status" },
    {
      value: "submitted",
      label: "Submitted",
      color: "bg-blue-100 text-blue-800",
    },
    {
      value: "medical_review_pending",
      label: "Pending Review",
      color: "bg-yellow-100 text-yellow-800",
    },
    {
      value: "under_medical_review",
      label: "Under Review",
      color: "bg-orange-100 text-orange-800",
    },
  ];

  const priorityOptions = [
    { value: "", label: "All Priority" },
    { value: "normal", label: "Normal", color: "bg-gray-100 text-gray-800" },
    { value: "high", label: "High", color: "bg-orange-100 text-orange-800" },
    { value: "urgent", label: "Urgent", color: "bg-red-100 text-red-800" },
  ];

  const treatmentTypes = [
    { value: "", label: "All Types" },
    { value: "medical", label: "Medical Treatment" },
    { value: "surgical", label: "Surgical Treatment" },
  ];

  const emergencyOptions = [
    { value: "", label: "All Cases" },
    { value: "true", label: "Emergency Only" },
    { value: "false", label: "Non-Emergency" },
  ];

  const pageSizeOptions = [
    { value: 10, label: "10 per page" },
    { value: 25, label: "25 per page" },
    { value: 50, label: "50 per page" },
    { value: 100, label: "100 per page" },
  ];

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    loadApplications();
  }, [filters, currentPage, pageSize, sortField, sortDirection]);

  const loadInitialData = async () => {
    try {
      // Load filter options
      const [hospitalsResult, companiesResult] = await Promise.all([
        getHospitalsForFilter(),
        getInsuranceCompaniesForFilter(),
      ]);

      if (hospitalsResult.success) {
        setHospitals(hospitalsResult.hospitals);
      }

      if (companiesResult.success) {
        setInsuranceCompanies(companiesResult.companies);
      }
    } catch (error) {
      console.error("Error loading initial data:", error);
    }
  };

  const loadApplications = async () => {
    setLoading(true);
    try {
      const result = await getAllApplicationsAdmin(
        filters,
        currentPage,
        pageSize
      );
      if (result.success) {
        setApplications(result.applications);
        setTotalPages(result.totalPages);
        setTotalCount(result.total);
      } else {
        console.error("Error loading applications:", result.error);
      }
    } catch (error) {
      console.error("Error loading applications:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => ({ ...prev, [filterType]: value }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handleClearFilters = () => {
    setFilters({
      searchTerm: "",
      status: "",
      priority: "",
      emergency: "",
      treatmentType: "",
      insuranceCompany: "",
      hospitalName: "",
      dateFrom: "",
      dateTo: "",
      minAmount: "",
      maxAmount: "",
      doctorName: "",
    });
    setCurrentPage(1);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
    setCurrentPage(1);
  };

  const handleStartReview = async (applicationId) => {
    setActionLoading((prev) => ({ ...prev, [applicationId]: true }));
    try {
      const result = await startApplicationReview(applicationId, user.uid);
      if (result.success) {
        navigate(`/doctor/application-review/${applicationId}`);
      } else {
        alert("Error starting review: " + result.error);
      }
    } catch (error) {
      console.error("Error starting review:", error);
      alert("Failed to start review");
    } finally {
      setActionLoading((prev) => ({ ...prev, [applicationId]: false }));
    }
  };

  const handleViewApplication = (applicationId) => {
    console.log("Viewing application:", applicationId);
    navigate(`/doctor/application-review/${applicationId}`);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const getStatusColor = (status) => {
    const statusOption = statusOptions.find((s) => s.value === status);
    return statusOption ? statusOption.color : "bg-gray-100 text-gray-800";
  };

  const getPriorityColor = (priority) => {
    const priorityOption = priorityOptions.find((p) => p.value === priority);
    return priorityOption ? priorityOption.color : "bg-gray-100 text-gray-800";
  };

  const handleExportApplications = () => {
    try {
      const result = exportPatientApplications(applications, 'patient_applications_admin');
      if (result.success) {
        alert(result.message);
      } else {
        alert('Export failed: ' + result.error);
      }
    } catch (error) {
      console.error('Error exporting patient applications:', error);
      alert('Failed to export applications');
    }
  };

  const getTimeSinceSubmission = (timestamp) => {
    if (!timestamp) return "N/A";
    const submittedDate = timestamp.toDate
      ? timestamp.toDate()
      : new Date(timestamp);
    const now = new Date();
    const diffMs = now - submittedDate;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) {
      return `${diffDays}d ago`;
    } else if (diffHours > 0) {
      return `${diffHours}h ago`;
    } else {
      const diffMins = Math.floor(diffMs / (1000 * 60));
      return `${diffMins}m ago`;
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDirection === "asc" ? (
      <SortAsc className="h-3 w-3 ml-1" />
    ) : (
      <SortDesc className="h-3 w-3 ml-1" />
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Applications for Review
              </h1>
              <p className="text-gray-600">
                Review and process health insurance applications
              </p>
            </div>

            <div className="flex items-center space-x-4">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`btn-outline ${showFilters ? "bg-teal-50 border-teal-300 text-teal-700" : ""
                  }`}
              >
                <Filter className="h-4 w-4 mr-2" />
                Filters
                {Object.values(filters).some((f) => f !== "") && (
                  <span className="ml-2 bg-teal-500 text-white text-xs rounded-full px-2 py-1">
                    {Object.values(filters).filter((f) => f !== "").length}
                  </span>
                )}
              </button>

              <button onClick={loadApplications} className="btn-outline">
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </button>

              <button 
                onClick={handleExportApplications}
                className="btn-outline"
              >
                <Download className="h-4 w-4 mr-2" />
                Export
              </button>
              
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Advanced Filters Panel */}
        {showFilters && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Filter Applications
              </h3>
              <button
                onClick={() => setShowFilters(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-6">
              {/* Search */}
              <div className="xl:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Search Applications
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by ID, patient name, diagnosis..."
                    value={filters.searchTerm}
                    onChange={(e) =>
                      handleFilterChange("searchTerm", e.target.value)
                    }
                    className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange("status", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                >
                  {statusOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Priority */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Priority
                </label>
                <select
                  value={filters.priority}
                  onChange={(e) =>
                    handleFilterChange("priority", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                >
                  {priorityOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Emergency */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Emergency Cases
                </label>
                <select
                  value={filters.emergency}
                  onChange={(e) =>
                    handleFilterChange("emergency", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                >
                  {emergencyOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Treatment Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Treatment Type
                </label>
                <select
                  value={filters.treatmentType}
                  onChange={(e) =>
                    handleFilterChange("treatmentType", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                >
                  {treatmentTypes.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Hospital */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Hospital
                </label>
                <select
                  value={filters.hospitalName}
                  onChange={(e) =>
                    handleFilterChange("hospitalName", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                >
                  <option value="">All Hospitals</option>
                  {hospitals.map((hospital) => (
                    <option key={hospital.id} value={hospital.name}>
                      {hospital.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Insurance Company */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Insurance Company
                </label>
                <select
                  value={filters.insuranceCompany}
                  onChange={(e) =>
                    handleFilterChange("insuranceCompany", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                >
                  <option value="">All Companies</option>
                  {insuranceCompanies.map((company) => (
                    <option key={company} value={company}>
                      {company}
                    </option>
                  ))}
                </select>
              </div>

              {/* Date From */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date From
                </label>
                <input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) =>
                    handleFilterChange("dateFrom", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                />
              </div>

              {/* Date To */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date To
                </label>
                <input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => handleFilterChange("dateTo", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                />
              </div>

              {/* Min Amount */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Min Amount (₹)
                </label>
                <input
                  type="number"
                  placeholder="0"
                  value={filters.minAmount}
                  onChange={(e) =>
                    handleFilterChange("minAmount", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                />
              </div>

              {/* Max Amount */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max Amount (₹)
                </label>
                <input
                  type="number"
                  placeholder="1000000"
                  value={filters.maxAmount}
                  onChange={(e) =>
                    handleFilterChange("maxAmount", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <button
                onClick={handleClearFilters}
                className="btn-outline text-sm"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Clear All Filters
              </button>

              <div className="text-sm text-gray-600">
                {totalCount} applications found
              </div>
            </div>
          </div>
        )}

        {/* Results Summary */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              <div className="text-sm text-gray-600">
                Showing{" "}
                <span className="font-medium">
                  {(currentPage - 1) * pageSize + 1}
                </span>{" "}
                to{" "}
                <span className="font-medium">
                  {Math.min(currentPage * pageSize, totalCount)}
                </span>{" "}
                of <span className="font-medium">{totalCount}</span>{" "}
                applications
              </div>

              {/* Page Size Selector */}
              <div className="flex items-center space-x-2">
                <label className="text-sm text-gray-600">Show:</label>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="text-sm border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                >
                  {pageSizeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Emergency Count */}
            {applications.filter((app) => app.emergency).length > 0 && (
              <div className="flex items-center space-x-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                <AlertTriangle className="h-4 w-4 text-red-500" />
                <span className="text-sm font-medium text-red-700">
                  {applications.filter((app) => app.emergency).length} Emergency
                  Cases
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Applications Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-teal-500" />
              <span className="ml-3 text-gray-600">
                Loading applications...
              </span>
            </div>
          ) : applications.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No Applications Found
              </h3>
              <p className="text-gray-600 mb-4">
                No applications match your current filters.
              </p>
              <button onClick={handleClearFilters} className="btn-primary">
                Clear Filters
              </button>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort("applicationId")}
                      >
                        <div className="flex items-center">
                          Application
                          <SortIcon field="applicationId" />
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort("patient.name")}
                      >
                        <div className="flex items-center">
                          Patient Details
                          <SortIcon field="patient.name" />
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Medical Information
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Hospital & Doctor
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort("medical.estimatedCost")}
                      >
                        <div className="flex items-center">
                          Financial Details
                          <SortIcon field="medical.estimatedCost" />
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Priority & Status
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort("submittedAt")}
                      >
                        <div className="flex items-center">
                          Timeline
                          <SortIcon field="submittedAt" />
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {applications.map((application) => (
                      <tr
                        key={application.id}
                        className={`hover:bg-gray-50 transition-colors ${application.emergency
                          ? "bg-red-50 border-l-4 border-red-400"
                          : ""
                          }`}
                      >
                        {/* Application ID */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {application.applicationId}
                            </div>
                            <div className="text-xs text-gray-500">
                              ID: {application.id?.slice(-8)}
                            </div>
                            {application.workflow?.medicalReview?.status ===
                              "in_progress" && (
                                <div className="flex items-center mt-1">
                                  <Timer className="h-3 w-3 text-orange-500 mr-1" />
                                  <span className="text-xs text-orange-600 font-medium">
                                    Under Review
                                  </span>
                                </div>
                              )}
                          </div>
                        </td>

                        {/* Patient Details */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center mr-3">
                              <User className="h-4 w-4 text-teal-600" />
                            </div>
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {application.patient?.name}
                              </div>
                              <div className="text-sm text-gray-500">
                                {application.patient?.age}Y,{" "}
                                {application.patient?.gender}
                              </div>
                              <div className="flex items-center text-xs text-gray-400 mt-1">
                                <Phone className="h-3 w-3 mr-1" />
                                {application.patient?.phone}
                              </div>
                            </div>
                          </div>
                        </td>

                        {/* Medical Information */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {application.medical?.diagnosis}
                            </div>
                            <div className="flex items-center space-x-2 mt-1">
                              <span
                                className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${application.medical?.treatmentType ===
                                  "surgical"
                                  ? "bg-red-100 text-red-800"
                                  : "bg-blue-100 text-blue-800"
                                  }`}
                              >
                                {application.medical?.treatmentType?.toUpperCase()}
                              </span>
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              Admission: {application.medical?.admissionDate}
                            </div>
                          </div>
                        </td>

                        {/* Hospital & Doctor */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="flex items-center text-sm text-gray-900 mb-1">
                              <Building className="h-3 w-3 mr-1 text-gray-400" />
                              {application.hospitalName}
                            </div>
                            <div className="text-sm text-gray-600">
                              Dr. {application.medical?.doctor?.name}
                            </div>
                            <div className="text-xs text-gray-500">
                              {application.medical?.doctor?.designation}
                            </div>
                          </div>
                        </td>

                        {/* Financial Details */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="flex items-center text-sm font-medium text-teal-600 mb-1">
                              <DollarSign className="h-3 w-3 mr-1" />
                              {formatCurrency(
                                application.medical?.estimatedCost
                              )}
                            </div>
                            <div className="flex items-center text-xs text-gray-500">
                              <CreditCard className="h-3 w-3 mr-1" />
                              {application.policy?.insuranceCompany}
                            </div>
                            <div className="text-xs text-gray-400 font-mono">
                              {application.policy?.number}
                            </div>
                          </div>
                        </td>

                        {/* Priority & Status */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="space-y-2">
                            <div className="flex items-center space-x-2">
                              <span
                                className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(
                                  application.priority
                                )}`}
                              >
                                {application.priority?.charAt(0).toUpperCase() +
                                  application.priority?.slice(1)}
                              </span>
                              {application.emergency && (
                                <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                                  <AlertTriangle className="h-3 w-3 mr-1" />
                                  Emergency
                                </span>
                              )}
                            </div>
                            <div>
                              <span
                                className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                                  application.status
                                )}`}
                              >
                                {application.status
                                  ?.replace(/_/g, " ")
                                  ?.toUpperCase()}
                              </span>
                            </div>
                          </div>
                        </td>

                        {/* Timeline */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="flex items-center text-sm text-gray-500 mb-1">
                              <Calendar className="h-3 w-3 mr-1" />
                              {formatDate(application.submittedAt)}
                            </div>
                            <div className="text-xs text-gray-400">
                              {getTimeSinceSubmission(application.submittedAt)}
                            </div>
                            {application.workflow?.medicalReview?.status ===
                              "in_progress" && (
                                <div className="flex items-center text-xs text-orange-600 mt-1">
                                  <Activity className="h-3 w-3 mr-1 animate-pulse" />
                                  Active Review
                                </div>
                              )}
                          </div>
                        </td>

                        {/* Actions */}
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() =>
                                handleViewApplication(application.applicationId)
                              }
                              className="text-teal-600 hover:text-teal-900 p-1 rounded-full hover:bg-teal-50"
                              title="View Details"
                            >
                              <Eye className="h-4 w-4" />
                            </button>

                            {/* {application.workflow?.medicalReview?.status !==
                              "in_progress" ? (
                              <button
                                onClick={() =>
                                  handleStartReview(application.applicationId)
                                }
                                disabled={
                                  actionLoading[application.applicationId]
                                }
                                className="text-green-600 hover:text-green-900 p-1 rounded-full hover:bg-green-50 disabled:opacity-50"
                                title="Start Review"
                              >
                                {actionLoading[application.applicationId] ? (
                                  <RefreshCw className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Play className="h-4 w-4" />
                                )}
                              </button>
                            ) : application.workflow?.medicalReview
                              ?.reviewerId === user.uid ? (
                              <button
                                onClick={() =>
                                  navigate(
                                    `/doctor/application-review/${application.applicationId}`
                                  )
                                }
                                className="text-orange-600 hover:text-orange-900 p-1 rounded-full hover:bg-orange-50"
                                title="Continue Review"
                              >
                                <Timer className="h-4 w-4" />
                              </button>
                            ) : (
                              <span
                                className="text-yellow-600 p-1"
                                title="Under review by another doctor"
                              >
                                <Pause className="h-4 w-4" />
                              </span>
                            )} */}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="bg-white px-6 py-4 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setCurrentPage(1)}
                        disabled={currentPage === 1}
                        className="btn-outline text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        First
                      </button>
                      <button
                        onClick={() =>
                          setCurrentPage((prev) => Math.max(prev - 1, 1))
                        }
                        disabled={currentPage === 1}
                        className="btn-outline text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronLeft className="h-4 w-4 mr-1" />
                        Previous
                      </button>
                    </div>

                    <div className="flex items-center space-x-2">
                      {/* Page Numbers */}
                      {Array.from(
                        { length: Math.min(5, totalPages) },
                        (_, i) => {
                          let pageNum;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }

                          return (
                            <button
                              key={pageNum}
                              onClick={() => setCurrentPage(pageNum)}
                              className={`px-3 py-2 text-sm rounded-lg border transition-colors ${currentPage === pageNum
                                ? "bg-teal-500 text-white border-teal-500"
                                : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                                }`}
                            >
                              {pageNum}
                            </button>
                          );
                        }
                      )}
                    </div>

                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() =>
                          setCurrentPage((prev) =>
                            Math.min(prev + 1, totalPages)
                          )
                        }
                        disabled={currentPage === totalPages}
                        className="btn-outline text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </button>
                      <button
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={currentPage === totalPages}
                        className="btn-outline text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Last
                      </button>
                    </div>
                  </div>

                  <div className="mt-3 text-center text-sm text-gray-600">
                    Page {currentPage} of {totalPages} • {totalCount} total
                    applications
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Quick Stats Footer */}
        <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">
                {totalCount}
              </div>
              <div className="text-sm text-gray-600">Total Applications</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">
                {applications.filter((app) => app.emergency).length}
              </div>
              <div className="text-sm text-gray-600">Emergency Cases</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600">
                {applications.filter((app) => app.priority === "urgent").length}
              </div>
              <div className="text-sm text-gray-600">Urgent Priority</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {
                  applications.filter(
                    (app) => app.medical?.treatmentType === "surgical"
                  ).length
                }
              </div>
              <div className="text-sm text-gray-600">Surgical Cases</div>
            </div>
          </div>
        </div>

        {/* Floating Action Button for Emergency Cases */}
        {applications.filter((app) => app.emergency).length > 0 && (
          <div className="fixed bottom-6 right-6">
            <button
              onClick={() => {
                setFilters((prev) => ({ ...prev, emergency: "true" }));
                setShowFilters(true);
              }}
              className="bg-red-500 hover:bg-red-600 text-white rounded-full p-4 shadow-lg transition-all duration-200 hover:scale-105"
              title="Filter Emergency Cases"
            >
              <div className="flex items-center space-x-2">
                <AlertTriangle className="h-5 w-5" />
                <span className="font-medium">
                  {applications.filter((app) => app.emergency).length} Emergency
                </span>
              </div>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminApplicationsList;
